// Voice Lead-In Lab — Sep 29 2026 (Randy pt-voice-clip-lab).
//
// The clipping he reports on Windows (Chrome AND Edge, every play, iPhone
// fine) cannot be judged from preview: it lives in whatever sits between
// Chrome's audio graph and his speakers. So this is the instrument instead of
// another guess — it speaks a numbered count-in through the REAL premium
// path, records what the audio graph hands over on THIS machine, and exposes
// the four dials that playback actually reads (lib/voicePlayback.js).
//
// Tap "Apply to my playback" and the dials take effect everywhere premium
// voice plays on this device (public Q&A, AI Assistant, whole-conversation).
// Whichever combination stops the clipping becomes the platform default.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { SpeakerHigh, Waveform, ArrowCounterClockwise, FloppyDisk, Copy } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  VOICE_TUNING_DEFAULTS, getVoiceTuning, setVoiceTuning, resetVoiceTuning,
  isVoiceTuningOverridden, startRouteHold, buildChunkSource, b64ToArrayBuffer,
} from "@/lib/voicePlayback";

const COUNT_IN = "One. Two. Three. Four. Five. Six. Seven. Eight. Nine. Ten.";
const LEADS = [0, 300, 650, 1000, 1500, 2500];
const DITHERS = [
  { v: -999, l: "off · silence" },
  { v: -80, l: "-80 dB" },
  { v: -60, l: "-60 dB" },
  { v: -45, l: "-45 dB" },
];
const AHEADS = [0, 120, 250, 400];

const bins = (buf, ms = 100) => {
  const d = buf.getChannelData(0);
  const w = Math.floor(buf.sampleRate * (ms / 1000));
  const out = [];
  for (let i = 0; i < d.length; i += w) {
    let s = 0, n = 0;
    for (let k = i; k < Math.min(i + w, d.length); k++) { s += d[k] * d[k]; n++; }
    out.push(Math.sqrt(s / Math.max(1, n)));
  }
  return out;
};
const firstAudible = (env, thr = 0.01) => {
  const i = env.findIndex((v) => v > thr);
  return i < 0 ? null : i * 100;
};

export default function VoiceLeadInLab() {
  const [silos, setSilos] = useState([]);
  const [slug, setSlug] = useState("");
  const [tuning, setTuning] = useState(() => getVoiceTuning());
  const [overridden, setOverridden] = useState(() => isVoiceTuningOverridden());
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState("");
  const ctxRef = useRef(null);
  const audioRef = useRef({ slug: null, bytes: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/silos");
        const list = (r.data || []).map((s) => ({ slug: s.slug, name: s.name }));
        setSilos(list);
        if (list.length) setSlug(list[0].slug);
      } catch { /* silent — the lab is optional */ }
    })();
  }, []);

  const ensureCtx = async () => {
    if (!ctxRef.current || ctxRef.current.state === "closed") {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new Ctx();
    }
    try { await ctxRef.current.resume(); } catch { /* */ }
    return ctxRef.current;
  };

  const loadBytes = async () => {
    if (audioRef.current.slug === slug && audioRef.current.bytes) return audioRef.current.bytes;
    const r = await api.post(`/s/${slug}/voice/speak`, { text: COUNT_IN, payer: "sponsored" }, { timeout: 60000 });
    const b64 = r.data?.audio_base64;
    if (!b64) throw new Error("No audio came back for this silo.");
    const bytes = new Uint8Array(b64ToArrayBuffer(b64));
    audioRef.current = { slug, bytes };
    return bytes;
  };

  const play = async () => {
    if (!slug) return toast.error("Pick a silo first.");
    setBusy("play");
    try {
      const bytes = await loadBytes();
      const ctx = await ensureCtx();
      const stopHold = startRouteHold(ctx, tuning);
      const buf = await ctx.decodeAudioData(bytes.slice().buffer);
      const { src, startAt, audibleDelayMs } = buildChunkSource(ctx, buf, tuning, { withLead: true });
      src.connect(ctx.destination);
      src.start(startAt);
      setTimeout(() => stopHold && stopHold(), audibleDelayMs);
      toast.success("Playing — which number do you hear FIRST?");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Count-in failed.");
    } finally { setBusy(""); }
  };

  const measure = async () => {
    if (!slug) return toast.error("Pick a silo first.");
    setBusy("measure");
    setReport("");
    try {
      const bytes = await loadBytes();
      const ctx = await ensureCtx();
      const stopHold = startRouteHold(ctx, tuning);
      const buf = await ctx.decodeAudioData(bytes.slice().buffer);
      const srcEnv = bins(buf);
      const { src, startAt, audibleDelayMs } = buildChunkSource(ctx, buf, tuning, { withLead: true });
      const tap = ctx.createMediaStreamDestination();
      src.connect(ctx.destination);
      src.connect(tap);
      const chunks = [];
      const rec = new MediaRecorder(tap.stream);
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.start();
      src.start(startAt);
      setTimeout(() => stopHold && stopHold(), audibleDelayMs);
      const ts = ctx.getOutputTimestamp ? ctx.getOutputTimestamp() : null;
      const renderAhead = ts && ts.contextTime ? (ctx.currentTime - ts.contextTime) * 1000 : null;
      await new Promise((res) => setTimeout(res, (buf.duration + audibleDelayMs / 1000 + 0.9) * 1000));
      rec.stop();
      await new Promise((res) => { rec.onstop = res; });
      const recBuf = await ctx.decodeAudioData(await new Blob(chunks).arrayBuffer());
      const recEnv = bins(recBuf);
      const srcStart = firstAudible(srcEnv);
      const recStart = firstAudible(recEnv);
      const drift = recStart == null || srcStart == null ? null : recStart - audibleDelayMs - srcStart;
      const lines = [
        `device   : ${ctx.sampleRate} Hz · baseLatency ${Math.round((ctx.baseLatency || 0) * 1000)}ms · outputLatency ${Math.round((ctx.outputLatency || 0) * 1000)}ms${renderAhead == null ? "" : ` · render-ahead ${Math.round(renderAhead)}ms`}`,
        `dials    : lead ${tuning.leadMs}ms · hold ${tuning.routeHold ? "on" : "off"} @ ${tuning.ditherDb <= -200 ? "silence" : `${tuning.ditherDb} dB`} · schedule-ahead ${tuning.scheduleAheadMs}ms`,
        `clip     : decoded ${buf.duration.toFixed(3)}s · first word at ${srcStart}ms into the file`,
        `graph    : recorded ${recBuf.duration.toFixed(3)}s · first audible at ${recStart}ms (expected ${audibleDelayMs + (srcStart || 0)}ms)`,
        drift == null
          ? "verdict  : could not read the recording — try again."
          : Math.abs(drift) <= 150
            ? "verdict  : the audio graph hands over EVERY word. Anything you still don't hear is being dropped after Chrome (output route / driver) → raise the hold level and/or the lead."
            : `verdict  : the graph itself is ${drift > 0 ? "losing" : "gaining"} ${Math.abs(Math.round(drift))}ms before the destination → raise schedule-ahead.`,
      ];
      setReport(lines.join("\n"));
    } catch (e) {
      setReport(`measure failed: ${e.message || e}`);
    } finally { setBusy(""); }
  };

  const apply = () => { setVoiceTuning(tuning); setOverridden(true); toast.success("Applied to premium voice on this device."); };
  const reset = () => { const d = resetVoiceTuning(); setTuning(d); setOverridden(false); toast.success("Back to the platform default."); };

  const Pills = ({ label, values, current, onPick, fmt, testid }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] w-[118px] shrink-0">{label}</span>
      {values.map((v) => {
        const val = typeof v === "object" ? v.v : v;
        const lbl = typeof v === "object" ? v.l : fmt(v);
        const on = current === val;
        return (
          <button
            key={String(val)}
            type="button"
            onClick={() => onPick(val)}
            data-testid={`${testid}-${val}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#2F6B3A] text-white border-[#2F6B3A]" : "bg-white text-[#4A5D4E] hairline hover:bg-[#F0F1EE]"}`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="mt-6 p-4 bg-white border hairline rounded-lg" data-testid="voice-leadin-lab">
      <div className="flex items-center gap-2">
        <Waveform size={16} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="text-[13px] font-semibold text-[#1C1B1A]">Voice Lead-In Lab</h3>
        {overridden && (
          <span className="text-[9.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#FFF3EB] text-[#7A3A1C]" data-testid="voice-lab-override-pill">
            device override active
          </span>
        )}
      </div>
      <p className="text-[11.5px] text-[#6B6A68] mt-1 leading-relaxed">
        Plays “One. Two. Three… Ten.” through the real premium-voice path. The number you hear
        <strong> first</strong> is how much is being lost — each count is ~0.32s. The dials below are the
        ones playback actually reads, so whatever combination plays clean here is the fix.
        Synthesis is billed once per silo, then it's cached for 24h and free.
      </p>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] w-[118px] shrink-0">Silo voice</span>
        <select
          value={slug}
          onChange={(e) => { setSlug(e.target.value); audioRef.current = { slug: null, bytes: null }; }}
          data-testid="voice-lab-silo"
          className="text-[12px] border hairline rounded-md px-2 py-1 bg-white"
        >
          {silos.length === 0 && <option value="">(no silos)</option>}
          {silos.map((s) => <option key={s.slug} value={s.slug}>{s.name} · /{s.slug}</option>)}
        </select>
      </div>

      <div className="mt-3 space-y-2">
        <Pills label="Lead-in" values={LEADS} current={tuning.leadMs} fmt={(v) => `${v}ms`} testid="voice-lab-lead"
          onPick={(v) => setTuning((t) => ({ ...t, leadMs: v }))} />
        <Pills label="Hold level" values={DITHERS} current={tuning.ditherDb} fmt={(v) => `${v}`} testid="voice-lab-dither"
          onPick={(v) => setTuning((t) => ({ ...t, ditherDb: v }))} />
        <Pills label="Route hold" values={[{ v: true, l: "on" }, { v: false, l: "off" }]} current={tuning.routeHold} fmt={String} testid="voice-lab-hold"
          onPick={(v) => setTuning((t) => ({ ...t, routeHold: v }))} />
        <Pills label="Schedule ahead" values={AHEADS} current={tuning.scheduleAheadMs} fmt={(v) => `${v}ms`} testid="voice-lab-ahead"
          onPick={(v) => setTuning((t) => ({ ...t, scheduleAheadMs: v }))} />
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={play} disabled={!!busy} data-testid="voice-lab-play"
          className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1">
          <SpeakerHigh size={12} weight="fill" /> {busy === "play" ? "Playing…" : "Play count-in"}
        </button>
        <button type="button" onClick={measure} disabled={!!busy} data-testid="voice-lab-measure"
          className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1">
          <Waveform size={12} weight="duotone" /> {busy === "measure" ? "Measuring…" : "Measure this device"}
        </button>
        <button type="button" onClick={apply} data-testid="voice-lab-apply"
          className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#2F6B3A] hover:bg-[#EAF3EC] flex items-center gap-1">
          <FloppyDisk size={12} weight="duotone" /> Apply to my playback
        </button>
        <button type="button" onClick={reset} data-testid="voice-lab-reset"
          className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:bg-[#F0F1EE] flex items-center gap-1">
          <ArrowCounterClockwise size={12} weight="duotone" /> Platform default
        </button>
        <span className="text-[10.5px] text-[#6B6A68]">
          default: lead {VOICE_TUNING_DEFAULTS.leadMs}ms · hold on · silence · ahead 0ms
        </span>
      </div>

      {report && (
        <div className="mt-3 p-3 bg-[#FDFCF8] border hairline rounded-md">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Measurement</span>
            <button type="button" data-testid="voice-lab-copy" onClick={() => { navigator.clipboard?.writeText(report); toast.success("Copied."); }}
              className="text-[10.5px] text-[#4A5D4E] hover:underline flex items-center gap-1">
              <Copy size={11} weight="duotone" /> Copy
            </button>
          </div>
          <pre className="text-[11px] font-mono text-[#1C1B1A] whitespace-pre-wrap" data-testid="voice-lab-report">{report}</pre>
        </div>
      )}
    </section>
  );
}
